import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useCart } from '../hooks/useCart'
import * as db from '../services/db'
import type { Product } from '../types'
import { playScanSound } from '../services/sounds'
import { hapticLight } from '../utils/haptic'
import { Toast } from '../components/Toast'
import { QuantityPicker } from '../components/QuantityPicker'

export function CategoryView() {
  const { category } = useParams<{ category: string }>()
  const { addByProductIdWithQuantity } = useCart()
  const [list, setList] = useState<Product[]>([])
  const [quantityProduct, setQuantityProduct] = useState<Product | null>(null)
  const [toastVisible, setToastVisible] = useState(false)
  const [toastMessage, setToastMessage] = useState('Added! 🎉')
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!category) return
    setLoaded(false)
    const normalizedCategory = category.trim()
    db.getAllProducts().then((all) => {
      setList(
        all.filter(
          (p) => p.category.toLowerCase() === normalizedCategory.toLowerCase()
        )
      )
      setLoaded(true)
    })
  }, [category])

  const handleTap = (product: Product) => {
    setQuantityProduct(product)
  }

  const handleQuantityAdd = async (quantity: number) => {
    if (!quantityProduct) return
    const ok = await addByProductIdWithQuantity(quantityProduct.id, quantity)
    if (ok) {
      playScanSound()
      hapticLight()
      setToastMessage(quantity > 1 ? `${quantity} added! 🎉` : 'Added! 🎉')
      setToastVisible(true)
    }
    setQuantityProduct(null)
  }

  const title = category ?? 'Browse'

  if (loaded && list.length === 0 && category) {
    return (
      <div className="page page--category">
        <header className="category-header">
          <Link to="/grocery" className="btn-back" aria-label="Back to home">←</Link>
          <h1 className="page-title">{title}</h1>
        </header>
        <div className="empty-state">
          <span className="empty-state-emoji" aria-hidden>📦</span>
          <h2 className="empty-state-title">No items here yet</h2>
          <p className="empty-state-p">This category is empty. Try another category or scan an item!</p>
          <Link to="/grocery" className="btn-large btn-primary" style={{ textDecoration: 'none' }}>Home</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="page page--category">
      {quantityProduct && (
        <QuantityPicker
          product={quantityProduct}
          onAdd={handleQuantityAdd}
          onCancel={() => setQuantityProduct(null)}
        />
      )}
      <Toast message={toastMessage} visible={toastVisible} onDismiss={() => setToastVisible(false)} />
      <header className="category-header">
        <Link to="/grocery" className="btn-back" aria-label="Back to home">←</Link>
        <h1 className="page-title">{title}</h1>
      </header>
      <div className="category-grid">
        {list.map((product) => (
          <button
            key={product.id}
            type="button"
            className="category-card"
            onClick={() => handleTap(product)}
            aria-label={`Add ${product.name} to cart, $${product.price.toFixed(2)}`}
          >
            <span className="category-card-emoji" aria-hidden>{product.imagePath}</span>
            <span className="category-card-name">{product.name}</span>
            <span className="category-card-price">${product.price.toFixed(2)}</span>
          </button>
        ))}
      </div>
      <Link to="/grocery" className="btn-large btn-secondary home-link" style={{ textDecoration: 'none' }}>Home</Link>
    </div>
  )
}
